import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/ui/components/Button';
import { Icon } from '@/ui/icons/Icon';
import { useLoanOffer } from '../../hooks/useLoanOffer';
import { DocumentInReview } from '@/components/DocumentInReview';
import { routes } from '@/routes/routes';
import styles from './ProofOfIncomePage.module.scss';

const REVIEW_POLL_MS = 15000;

export function ProofOfIncomePage() {
  const navigate = useNavigate();
  const {
    loanOfferState,
    getLoanOffersState,
    uploadProofOfIncome,
    isUploadingProofOfIncome,
  } = useLoanOffer(true);
  // Sent during this visit: under review, even before the offers state says.
  const [isSubmitted, setIsSubmitted] = useState(false);
  const isProcessing = !!loanOfferState?.proofOfIncomeProcessing || isSubmitted;

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (
      loanOfferState &&
      !loanOfferState.proofOfIncomeRequired &&
      !loanOfferState.proofOfIncomeProcessing
    ) {
      navigate(routes.wizard.base, { replace: true });
    }
  }, [loanOfferState]);

  useEffect(() => {
    if (!isProcessing) return;

    const intervalId = setInterval(getLoanOffersState, REVIEW_POLL_MS);
    return () => clearInterval(intervalId);
  }, [isProcessing]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setSelectedFile(file);
    e.target.value = '';
  };

  const handleContinue = async () => {
    if (!selectedFile) return;
    // A proof under review keeps the user here, on the review screen.
    await uploadProofOfIncome(selectedFile);
    setIsSubmitted(true);
  };

  if (isProcessing) {
    return (
      <DocumentInReview description="Analizăm informațiile oferite și te vom contacta în caz că e nevoie de mai multe detalii." />
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        <div className={styles.header}>
          <span className={styles.emoji} role="img" aria-label="confirmare venit">
            💼
          </span>
          <div className={styles.headerText}>
            <h1 className={styles.title}>Dovada veniturilor</h1>
            <p className={styles.subtitle}>
             Am depistat o problemă in procesul de verificare a salariului tău.
            </p>
          </div>
        </div>

        <div className={styles.cards}>
          <div className={styles.card}>
            <div className={styles.cardBody}>
              <p className={styles.cardTitle}>Trimite un extras de salariu</p>
              <p className={styles.cardDescription}>
                Te rugam să ne trimiți extrasul tău de salariu sau adeverința de salariat pe ultimele 3 luni.
              </p>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf"
              className={styles.fileInput}
              onChange={handleFileChange}
            />
            <Button
              trackingId="upload_proof_of_income"
              variant="tonal"
              text={selectedFile ? selectedFile.name : 'Încarcă document'}
              leftIcon={<Icon name="file-upload" size={16} />}
              className={styles.uploadButton}
              onClick={() => fileInputRef.current?.click()}
            />
          </div>
        </div>
      </div>

      <div className={styles.bottomSection}>
        <div className={styles.buttons}>
          <Button
            trackingId="submit_proof_of_income"
            variant="filled"
            text="Continuă"
            disabled={!selectedFile || isUploadingProofOfIncome}
            loading={isUploadingProofOfIncome}
            rightIcon={<Icon name="arrow-right" size={16} />}
            className={styles.continueButton}
            onClick={handleContinue}
          />
        </div>
      </div>
    </div>
  );
}
